import { Logger } from "@/apis/Logger";

/**
 * Minimum OTP length for FIDO2 security keys.
 * Security keys generate at least 40 characters for their one-time passwords.
 */
const MIN_SECURITY_KEY_OTP_LENGTH = 40;

/**
 * Maximum typing duration to distinguish hardware from manual input.
 * Security keys typically complete input in under 800ms, while human typing takes longer.
 */
const MAX_TYPING_DURATION_MS = 800;

/**
 * Result interface for OTP validation indicating whether input came from a security key or manual entry.
 * Contains validation status, detected source, and detailed reasoning for logging and debugging.
 */
export interface OTPValidationResult {
  /** Whether the OTP passed all validation checks for security key detection */
  isValid: boolean;
  /**
   * Detected source of the OTP input based on validation criteria:
   * - `security_key_detected`: Fast input (≤800ms), correct format (40+ lowercase letters), no paste events
   * - `manual_input`: Slow input, incorrect format, or paste detected - indicates human manual entry
   */
  source: "security_key_detected" | "manual_input";
  /** Array of validation messages explaining the result for logging and debugging purposes */
  reasons: string[];
}

/**
 * Data structure to collect timing and interaction metrics for OTP input analysis.
 * Used to determine if input came from a hardware security key or manual user entry.
 */
export interface InputTimingData {
  /** Timestamp when input started (milliseconds) */
  startTime: number;
  /** Timestamp when input completed (milliseconds) */
  endTime: number;
  /** Whether the input was pasted rather than typed */
  wasPasted: boolean;
}

/**
 * Regular expression pattern for validating security key OTP format.
 * Matches 40 or more lowercase English letters as generated by FIDO2 security keys.
 *
 * @example "eiifcbfhfdlkbihfbjvujidctejuihtchuljdlvikjha"
 */
const SECURITY_KEY_OTP_PATTERN = new RegExp(
  `^[a-z]{${MIN_SECURITY_KEY_OTP_LENGTH},}$`
);

/**
 * Validates OTP input to detect if it came from a security key or manual entry.
 *
 * Security keys are detected by analyzing three criteria:
 * 1. **Format**: Must be at least 40 letters (security key standard). Input is
 *    lowercased first — Windows types the OTP in uppercase when Caps Lock is on.
 * 2. **Timing**: Input completed in ≤800ms (indicates hardware auto-typing)
 * 3. **Input method**: No paste events (hardware keys type, don't paste)
 *
 * @param otp the OTP string to validate
 * @param timingData input timing and interaction data
 * @returns `OTPValidationResult` with validation status, detected source, and reasons
 */
export function validateOTP(
  otp: string,
  timingData: InputTimingData
): OTPValidationResult {
  const reasons: string[] = [];
  const duration = timingData.endTime - timingData.startTime;

  /**
   * Normalize case before format validation. On Windows, keyboard lock state
   * is global: with Caps Lock enabled, the OS translates the security key's
   * HID keystrokes to uppercase, so the OTP arrives in ALL CAPS. The OTP is
   * only used here as a hardware-input heuristic (never verified server-side),
   * so case carries no information and can be safely discarded.
   * Precedent: MidwayAccountUserPortalLambda's otpProcessor lowercases OTP
   * input for the same reason.
   */
  const normalizedOtp = otp.toLowerCase();

  // 1. Format validation
  const formatValid =
    SECURITY_KEY_OTP_PATTERN.test(normalizedOtp) &&
    normalizedOtp.length >= MIN_SECURITY_KEY_OTP_LENGTH;
  if (!formatValid) {
    reasons.push(
      `Invalid OTP format: expected at least ${MIN_SECURITY_KEY_OTP_LENGTH} lowercase letters`
    );
  }

  // 2. Timing validation (fast input suggests hardware)
  const timingValid = duration <= MAX_TYPING_DURATION_MS;
  if (!timingValid) {
    reasons.push(
      `Input duration too slow: ${duration}ms (hardware expected <${MAX_TYPING_DURATION_MS}ms)`
    );
  }

  // 3. Paste detection (hardware keys don't paste)
  const pasteValid = !timingData.wasPasted;
  if (!pasteValid) {
    reasons.push("Paste operation detected - hardware keys don't paste");
  }

  const isValid = formatValid && timingValid && pasteValid;

  if (isValid) {
    reasons.push("Valid security key OTP detected");
  }

  void Logger.info("OTP Validation completed", undefined, {
    isValid,
    duration,
    reasons,
    source: "otp-validation",
  });

  return {
    isValid,
    source: isValid ? "security_key_detected" : "manual_input",
    reasons,
  };
}
